import {
  Braces, Binary, Link2, Type, FileSearch, GitCompare, Hash, KeyRound, Sparkles, Calendar, Code2, Wand2, Send,
} from 'lucide-react';
import type { Tool } from '@/registry/types';
import { JsonFormatter } from './JsonFormatter';
import { Base64 } from './Base64';
import { UrlEncode } from './UrlEncode';
import { CaseConverter } from './CaseConverter';
import { WordCounter } from './WordCounter';
import { RegexTester } from './RegexTester';
import { TextDiff } from './TextDiff';
import { HashGenerator } from './HashGenerator';
import { JwtDecoder } from './JwtDecoder';
import { TextTransform } from './TextTransform';
import { CronBuilder } from './CronBuilder';
import { JsonPath } from './JsonPath';
import { SvgOptimizer } from './SvgOptimizer';
import { ApiTester } from './ApiTester';

export const textTools: Tool[] = [
  {
    id: 'json-format',
    name: 'JSON formatter',
    category: 'text',
    description: 'Format, minify, and validate JSON with structural stats.',
    keywords: ['json', 'format', 'pretty', 'minify', 'validate', 'beautify'],
    icon: Braces,
    component: JsonFormatter,
    featured: true,
    inBrowser: true,
  },
  {
    id: 'jsonpath',
    name: 'JSONPath query',
    category: 'text',
    description: 'Query JSON with JSONPath — filters, recursion, slicing.',
    keywords: ['jsonpath', 'jq', 'json', 'query', 'filter', 'select'],
    icon: Code2,
    component: JsonPath,
    inBrowser: true,
  },
  {
    id: 'api-tester',
    name: 'API request tester',
    category: 'text',
    description: 'Postman-lite — compose requests, view responses, copy as cURL.',
    keywords: ['api', 'rest', 'http', 'postman', 'request', 'fetch', 'curl'],
    icon: Send,
    component: ApiTester,
    featured: true,
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'base64',
    name: 'Base64 encode/decode',
    category: 'text',
    description: 'Encode and decode Base64 with URL-safe variant.',
    keywords: ['base64', 'b64', 'encode', 'decode', 'binary'],
    icon: Binary,
    component: Base64,
    inBrowser: true,
  },
  {
    id: 'url-encode',
    name: 'URL encode/decode',
    category: 'text',
    description: 'Percent-encode or decode URLs and components.',
    keywords: ['url', 'uri', 'encode', 'decode', 'percent'],
    icon: Link2,
    component: UrlEncode,
    inBrowser: true,
  },
  {
    id: 'case-converter',
    name: 'Case converter',
    category: 'text',
    description: '12 cases: camel, snake, kebab, constant, sentence, title, reverse…',
    keywords: ['case', 'camel', 'snake', 'kebab', 'pascal', 'upper', 'lower', 'title'],
    icon: Type,
    component: CaseConverter,
    featured: true,
    inBrowser: true,
  },
  {
    id: 'word-counter',
    name: 'Word & character counter',
    category: 'text',
    description: 'Words, characters, sentences, reading time, longest word.',
    keywords: ['word', 'character', 'count', 'reading', 'time', 'wpm'],
    icon: Hash,
    component: WordCounter,
    inBrowser: true,
  },
  {
    id: 'regex-tester',
    name: 'Regex tester',
    category: 'text',
    description: 'Live regex testing with flags, captures, and highlights.',
    keywords: ['regex', 'regular', 'expression', 'pattern', 'match', 'test'],
    icon: FileSearch,
    component: RegexTester,
    featured: true,
    inBrowser: true,
  },
  {
    id: 'text-diff',
    name: 'Text diff',
    category: 'text',
    description: 'Compare two texts with line-level additions and deletions.',
    keywords: ['diff', 'compare', 'difference', 'change'],
    icon: GitCompare,
    component: TextDiff,
    inBrowser: true,
  },
  {
    id: 'hash-generator',
    name: 'Hash generator',
    category: 'text',
    description: 'SHA-1/256/384/512 digests via Web Crypto.',
    keywords: ['hash', 'sha', 'digest', 'checksum', 'crypto'],
    icon: KeyRound,
    component: HashGenerator,
    inBrowser: true,
  },
  {
    id: 'jwt-decode',
    name: 'JWT decoder',
    category: 'text',
    description: 'Inspect JSON Web Token header, payload, and signature.',
    keywords: ['jwt', 'json', 'web', 'token', 'decode', 'auth'],
    icon: KeyRound,
    component: JwtDecoder,
    inBrowser: true,
  },
  {
    id: 'cron-builder',
    name: 'Cron expression builder',
    category: 'text',
    description: 'Compose, parse, and predict cron expressions with next firings.',
    keywords: ['cron', 'crontab', 'schedule', 'job', 'expression'],
    icon: Calendar,
    component: CronBuilder,
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'svg-optimizer',
    name: 'SVG optimizer',
    category: 'text',
    description: 'Strip comments, default attrs, editor metadata; round numbers.',
    keywords: ['svg', 'optimize', 'minify', 'clean', 'svgo'],
    icon: Wand2,
    component: SvgOptimizer,
    badge: 'new',
    inBrowser: true,
  },
  {
    id: 'text-transform',
    name: 'Whitespace · dedupe · sort',
    category: 'text',
    description: 'Trim, dedupe, sort, number, or reverse lines.',
    keywords: ['whitespace', 'trim', 'dedupe', 'duplicate', 'sort', 'lines'],
    icon: Sparkles,
    component: TextTransform,
    inBrowser: true,
  },
];
